/**
 * 페이지 버전 히스토리 모달.
 * slide_page_versions 테이블에 captureVersion 으로 자동 적재된 N 버전 카드 표시 + 복원.
 */
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { SlidePageVersion } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  slideId: string;
  pageId: string;
  open: boolean;
  onClose: () => void;
}

const SOURCE_LABEL: Record<SlidePageVersion['source'], string> = {
  manual: '수동 편집',
  tool_call: '모델 호출',
  rollback: '복원',
};
const SOURCE_COLOR: Record<SlidePageVersion['source'], string> = {
  manual: 'bg-blue-500/10 text-blue-600 dark:text-blue-300',
  tool_call: 'bg-violet-500/10 text-violet-600 dark:text-violet-300',
  rollback: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
};

export function PageVersionHistory({ slideId, pageId, open, onClose }: Props) {
  const qc = useQueryClient();
  const versionsQuery = useQuery({
    queryKey: ['slide-page-versions', slideId, pageId],
    queryFn: () => api.slidePageVersions(slideId, pageId),
    enabled: open,
    staleTime: 5_000,
  });
  const restoreMut = useMutation({
    mutationFn: (versionId: string) => api.slideRestorePageVersion(slideId, pageId, versionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slide', slideId] });
      qc.invalidateQueries({ queryKey: ['slide-page-versions', slideId, pageId] });
      onClose();
    },
  });

  // Esc 로 닫기.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const versions = versionsQuery.data?.versions ?? [];

  return createPortal(
    // biome-ignore lint/a11y/useSemanticElements: 모달 backdrop. 내부 dialog 마크업으로 충분.
    <div
      role="dialog"
      aria-modal="true"
      aria-label="페이지 버전 히스토리"
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-[2px] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-lg border border-border bg-surface shadow-2xl">
        <div className="flex items-center gap-2 px-4 h-11 border-b border-border shrink-0">
          <History className="size-4 text-foreground-muted" />
          <h2 className="text-sm font-medium">페이지 버전 히스토리</h2>
          <span className="text-xs text-foreground-muted">({versions.length})</span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="size-7 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted/60 hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {versionsQuery.isLoading ? (
            <p className="text-sm text-foreground-muted">불러오는 중…</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-foreground-muted">
              아직 적재된 버전이 없어요. 페이지를 한 번 이상 수정하면 자동으로 버전이 쌓입니다.
            </p>
          ) : (
            versions.map((v) => <VersionCard key={v.id} v={v} restoreMut={restoreMut} />)
          )}
        </div>

        <div className="px-4 h-10 border-t border-border text-xs text-foreground-muted flex items-center shrink-0">
          복원 시 현재 페이지 내용은 새 버전으로 보관 후 선택한 버전으로 되돌립니다.
        </div>
      </div>
    </div>,
    document.body,
  );
}

function VersionCard({
  v,
  restoreMut,
}: {
  v: SlidePageVersion;
  restoreMut: { mutate: (id: string) => void; isPending: boolean };
}) {
  const time = formatRelative(new Date(v.createdAt));
  const onRestore = () => {
    if (window.confirm(`버전 ${v.version} 으로 복원할까요?\n현재 내용은 새 버전으로 보관됩니다.`)) {
      restoreMut.mutate(v.id);
    }
  };
  return (
    <div className="rounded-md border border-border bg-surface-muted/20 p-3">
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-start gap-1 shrink-0">
          <span className="text-xs font-mono tabular-nums text-foreground-muted">v{v.version}</span>
          <span
            className={cn(
              'inline-flex items-center text-[10px] px-1.5 py-0.5 rounded',
              SOURCE_COLOR[v.source],
            )}
          >
            {SOURCE_LABEL[v.source]}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{v.preview || '(빈 페이지)'}</div>
          <div className="text-xs text-foreground-muted mt-0.5">
            {time}
            {v.createdByName && <> · {v.createdByName}</>}
            <span className="ml-2 text-[10px] uppercase tracking-wide">{v.mode}</span>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onRestore}
          disabled={restoreMut.isPending}
          aria-label={`버전 ${v.version} 복원`}
        >
          복원
        </Button>
      </div>
    </div>
  );
}

/** 한국어 상대시간 — 수동 구현 (작은 의존). */
function formatRelative(d: Date): string {
  const now = Date.now();
  const ms = now - d.getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return '방금 전';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}일 전`;
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
